import http from './http';

function handleError(error) {
  const resData = error?.response?.data;
  if (resData) {
    const msg = resData.message || error?.message || 'Request failed';
    const details = Array.isArray(resData.errors)
      ? resData.errors.map(e => (e.field ? `${e.field}: ${e.message}` : e.message)).join('; ')
      : (resData.error || '');
    const full = details ? `${msg} — ${details}` : msg;
    throw new Error(full);
  }
  throw new Error(error?.message || 'Request failed');
}

/**
 * Create a new doctor reference
 * @param {Object} payload - Reference data
 * @param {string} payload.patientId - Patient ID
 * @param {string} payload.assignedDoctorId - Assigned doctor ID
 * @param {string} [payload.speciality] - Specialist field
 * @param {string} [payload.area] - Area/location
 * @param {string} [payload.toDoctorName] - Referred doctor name
 * @param {string} [payload.address] - Referred doctor address
 * @param {string} [payload.time] - Reference time
 * @param {string} [payload.notes] - Additional notes
 * @param {boolean} [payload.printHistory] - Print history flag
 */
export async function createReference(payload) {
  try {
    const res = await http.post('/api/v1/references', payload);
    return { data: res.data?.data || res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get all doctor references with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} [params.patientId] - Filter by patient ID
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=10] - Items per page
 */
export async function getReferences(params = {}) {
  try {
    const res = await http.get('/api/v1/references', { params });
    return { data: res.data?.data || res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Get a single doctor reference by ID
 * @param {string} id - Reference ID
 */
export async function getReferenceById(id) {
  try {
    const res = await http.get(`/api/v1/references/${id}`);
    return { data: res.data?.data || res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Update a doctor reference
 * @param {string} id - Reference ID
 * @param {Object} payload - Updated reference data
 */
export async function updateReference(id, payload) {
  try {
    const res = await http.patch(`/api/v1/references/${id}`, payload);
    return { data: res.data?.data || res.data };
  } catch (e) {
    handleError(e);
  }
}

/**
 * Delete a doctor reference
 * @param {string} id - Reference ID
 */
export async function deleteReference(id) {
  try {
    const res = await http.delete(`/api/v1/references/${id}`);
    return { data: res.data?.data || res.data };
  } catch (e) {
    handleError(e);
  }
}

export const referenceAPI = {
  createReference,
  getReferences,
  getReferenceById,
  updateReference,
  deleteReference
};
